import { type KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pickLang } from '../lib/pick-lang.js';
import { matchesQuery } from '../lib/row-filter.js';
import {
  Combobox,
  type ComboboxHandle,
  ComboboxList,
  ComboboxNotice,
  ComboboxOption,
} from './combobox.js';

// #78-Muster: eine "vernuenftige Zahl" fuer die Kandidaten-Trefferliste — begrenzt UND
// angeschrieben (eine stille Kappung liest sich wie Vollstaendigkeit). Ein Wert, EINE Konstante
// fuer alle Aufrufer (Review-Fund Minor 2) — vorher stand derselbe Wert zweimal (hier und in der
// #105-Kopie in pages/defects/affected-section.tsx).
export const TEST_CASE_PICKER_LIMIT = 30;

export type TestCasePickerCandidate = {
  test_case_id: string;
  human_id: string;
  title_de: string;
  title_en: string;
};

// #82-Muster, herausgeloest aus catalog/e2e-tab.tsx (vormals `AddMemberPicker`). Review-Fund
// (Important 2): eine fast identische Kopie war fuer #105 („blockiert außerdem") in
// pages/defects/affected-section.tsx entstanden — derselbe Combobox-/matchesQuery-Aufbau, nur mit
// anderen Beschriftungen. `label`/`placeholder`/`addLabel` sind deshalb jetzt Props statt fest
// verdrahteter `t('catalog.e2e.…')`-Aufrufe: der Aufrufer bringt seine eigenen, zum Kontext
// passenden Texte mit (E2E-Mitglied vs. „blockiert außerdem"). `catalog.search.noResults`/
// `resultsLimited` bleiben absichtlich fest verdrahtet — das sind generische Listen-Suchhinweise
// ohne fachlichen Bezug zu Katalog oder Defects, beide Aufrufer wollen exakt denselben Text.
export function TestCasePicker({
  label,
  placeholder,
  addLabel,
  pending,
  candidates,
  onAdd,
}: {
  label: string;
  placeholder: string;
  addLabel: string;
  pending: boolean;
  candidates: TestCasePickerCandidate[];
  onAdd: (testCaseId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const comboRef = useRef<ComboboxHandle>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // M3 (Review-Finding, aus der urspruenglichen AddMemberPicker uebernommen): `choose()` schreibt
  // "<human_id> — <title_de>" ins Feld — ein zusammengesetzter String, den `matchesQuery` gegen
  // KEIN einzelnes Kandidatenfeld matcht. Ohne diese Ausnahme zeigte ein erneutes Fokussieren
  // (ohne weiteres Tippen) faelschlich "Kein Treffer", waehrend die Auswahl selbst weiter gueltig
  // war. `selectedLabel` haelt fest, WELCHER Text zur aktuellen Auswahl gehoert.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  // -1 ⇒ noch keine explizite Pfeiltasten-Navigation; der erste Treffer gilt trotzdem als aktiv.
  const [highlighted, setHighlighted] = useState(-1);
  const listboxId = useId();

  if (candidates.length === 0) return null;

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length > 0 && query !== selectedLabel;
  const filtered = candidates.filter((c) =>
    matchesQuery(trimmedQuery, c.human_id, c.title_de, c.title_en),
  );
  const shown = filtered.slice(0, TEST_CASE_PICKER_LIMIT);
  const activeIndex = highlighted >= 0 ? Math.min(highlighted, shown.length - 1) : 0;

  const choose = (c: TestCasePickerCandidate) => {
    const l = `${c.human_id} — ${pickLang(lang, c.title_de, c.title_en)}`;
    setQuery(l);
    setSelectedId(c.test_case_id);
    setSelectedLabel(l);
    setHighlighted(-1);
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (shown.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(Math.min(activeIndex + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(Math.max(activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      const target = shown[activeIndex];
      if (target) {
        e.preventDefault();
        choose(target);
        comboRef.current?.close();
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Combobox
        ref={comboRef}
        label={label}
        placeholder={placeholder}
        value={query}
        onChange={(value) => {
          setQuery(value);
          setSelectedId(null);
          setSelectedLabel(null);
          setHighlighted(-1);
        }}
        onKeyDown={onInputKeyDown}
        listboxId={listboxId}
        // I7: nur waehrend das Dropdown ueberhaupt Treffer zeigt — sonst zeigt aria-activedescendant
        // auf eine Option-Id, die gar nicht im DOM steht. Bedingtes Spread statt `activeId={...:
        // undefined}`, weil `exactOptionalPropertyTypes: true` ein explizites `undefined` fuer ein
        // optionales Prop nicht annimmt.
        {...(shown.length > 0 ? { activeId: `${listboxId}-option-${activeIndex}` } : {})}
        className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-body"
      >
        {({ open, close }) =>
          open && (shown.length > 0 || searching) ? (
            <ComboboxList id={listboxId}>
              {shown.map((c, i) => (
                <ComboboxOption
                  key={c.test_case_id}
                  id={`${listboxId}-option-${i}`}
                  selected={i === activeIndex}
                  onSelect={() => {
                    choose(c);
                    close();
                  }}
                  className={`block w-full px-2 py-1 text-left text-text-body hover:bg-brand-accent/10 ${
                    i === activeIndex ? 'bg-brand-accent/10' : ''
                  }`}
                >
                  {c.human_id} — {pickLang(lang, c.title_de, c.title_en)}
                </ComboboxOption>
              ))}
              {searching && filtered.length === 0 && (
                <ComboboxNotice>
                  {t('catalog.search.noResults', { query: trimmedQuery })}
                </ComboboxNotice>
              )}
              {filtered.length > shown.length && (
                <ComboboxNotice>
                  {t('catalog.search.resultsLimited', {
                    shown: shown.length,
                    total: filtered.length,
                  })}
                </ComboboxNotice>
              )}
            </ComboboxList>
          ) : null
        }
      </Combobox>
      <button
        type="button"
        disabled={!selectedId || pending}
        onClick={() => {
          if (!selectedId) return;
          onAdd(selectedId);
          setQuery('');
          setSelectedId(null);
          setSelectedLabel(null);
        }}
        className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-brand-accent hover:bg-brand-accent/10 disabled:opacity-40"
      >
        + {addLabel}
      </button>
    </div>
  );
}
